/* eslint-disable react/jsx-props-no-spreading */
import CURP_REGEX from '@constants/curp';
import { Countries } from '@constants/enums';
import { PhoneInput } from '@efex-components/phone/PhoneInput';
import { CountriesList, FederalEntitiesList } from '@efex-services/auth/types';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { FocusEvent, useEffect, useRef, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@efexteam/components';
import { CurrencyCountry } from '@efex-components/common/Form/config';
import { PUBLIC_URLS } from '@utils/urls';
import { PasswordInput } from '../../../../components/password';
import authSvc from '../../../../services/auth/authSvc';
import { capitalizeFirst } from '../../../../utils/formater';
import { StepsControl } from '../../constants';
import { useFormData } from '../context';
import useOrganizationId from '../../hooks/useOrganizationId';
import useWhiteLabelValidation from '../../hooks/useWhiteLabelValidation';
import { transformFormDataForSubmission } from '../../utils';
import { MasterAccountField } from '../components/MasterAccountField';
import {
  registerFormSchema,
  PASSWORD_SCHEMA,
} from '../../../../schemas/auth.schemas';
import { RegisterFormData } from '../../types';

interface ICompanyInformation {
  formStep: number;
  error?: string;
  errorCode?: string;
  nextFormStep: () => void;
  prevStep: () => void;
  isSubAccountEnabled?: boolean;
}

const CompanyInformation = ({
  formStep,
  error,
  errorCode,
  nextFormStep,
  prevStep,
  isSubAccountEnabled,
}: ICompanyInformation) => {
  const {
    setFormValues,
    data,
    countries,
    birthcountries,
    federalEntities,
    getCountries,
    getFederalEntities,
    getBirthCountries,
  } = useFormData();
  const { t } = useTranslation();
  const { organizationId } = useOrganizationId();
  const {
    isValidating: isValidatingMasterAccount,
    validationError: masterAccountValidationError,
    isValid: isMasterAccountValid,
    validateOrganization,
  } = useWhiteLabelValidation();

  const [enableSecondLastName, setEnableSecondLastName] = useState(true);

  const [validationError, setValidationError] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailExist, setEmailExist] = useState<string | undefined>(undefined);
  const [controlNationalityCountry, setControlNationalityCountry] =
    useState<CountriesList | null>();

  const inputCode = useRef<HTMLInputElement>(null);

  const {
    handleSubmit,
    formState: { errors, defaultValues },
    control,
    setValue,
    register,
    setFocus,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerFormSchema),
    mode: 'onChange',
    defaultValues: {
      ...data,
      masterAccountName: isSubAccountEnabled ? organizationId || '' : '',
    },
  });

  const validateReferralCode = async () => {
    if (!inputCode?.current?.value || inputCode.current.value.length < 6) {
      setValidationError(false);
      return;
    }
    try {
      const { result } = await authSvc.validateReferrer(
        inputCode?.current.value
      );
      setValue('referralId', result.toString());
      setValidationSuccess(true);
      setValidationError(false);
    } catch {
      setValidationError(true);
      setValidationSuccess(false);
    }
  };

  const masterAccountName = watch('masterAccountName');

  const isFormSubmissionDisabled = useMemo((): boolean => {
    if (isSubmitting) {
      return true;
    }

    const hasEmailError = !!emailExist;
    if (hasEmailError) {
      return true;
    }

    if (isSubAccountEnabled && !organizationId) {
      const isMasterAccountNameEmpty = !masterAccountName?.trim();
      const hasMasterAccountValidationError = !!masterAccountValidationError;
      const isMasterAccountInvalid = !isMasterAccountValid;

      const hasMasterAccountErrors =
        isMasterAccountNameEmpty ||
        hasMasterAccountValidationError ||
        isMasterAccountInvalid;

      return hasMasterAccountErrors;
    }

    return false;
  }, [
    isSubmitting,
    emailExist,
    isSubAccountEnabled,
    organizationId,
    masterAccountName,
    masterAccountValidationError,
    isMasterAccountValid,
  ]);

  const onSubmit = handleSubmit((values) => {
    const transformedData = transformFormDataForSubmission(
      values,
      isSubAccountEnabled,
      organizationId
    );

    setIsSubmitting(true);
    setFormValues(transformedData as unknown as Record<string, unknown>);
  });

  const handleBack = () => {
    prevStep();
  };

  const capitalizeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    const capitalized = value
      .split(' ')
      .map((subName) => capitalizeFirst(subName))
      .join(' ');
    setValue(name as 'firstName' | 'lastName', capitalized);
  };

  const validateRegisterEmail = async (event: FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    setEmailExist(undefined);
    try {
      const { value } = event.target;
      const resp = await authSvc.validateRegisterEmail(
        encodeURIComponent(value)
      );
      if (resp.message === 'Person exist') {
        setEmailExist('emailNonValid');
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const httpError = err as { response?: { data?: { message?: string } } };
        if (httpError?.response?.data?.message === 'Person not found') {
          setEmailExist(undefined);
        }
      }
    }
  };

  useEffect(() => {
    if (data.firstName && isSubmitting) {
      nextFormStep();
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSubmitting, setFocus, isSubAccountEnabled, organizationId]);

  useEffect(() => {
    if (defaultValues?.countryBirthId) {
      getBirthCountries(defaultValues?.countryBirthId);
      setValue('countryBirthId', defaultValues?.countryBirthId);
    }
    if (defaultValues?.nationalityPersonId) {
      getCountries(defaultValues?.nationalityPersonId);
      setValue('nationalityPersonId', defaultValues?.nationalityPersonId);
    }
    if (defaultValues?.federalEntityId) {
      getFederalEntities(defaultValues?.federalEntityId);
      setValue('federalEntityId', defaultValues?.federalEntityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  if (formStep !== StepsControl.CompanyInformation) return null;

  return (
    <>
      <Typography variant="h4" className="!my-4 text-center">
        {t('regCompanyTitle')}
      </Typography>

      <form
        onSubmit={onSubmit}
        className="flex gap-4 flex-col"
        autoComplete="off"
      >
        <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
          <Grid item xs={12}>
            <Typography variant="h6">{t('companyInformationTitle')}</Typography>

            <Alert severity="info">{t('regNameMessage')}</Alert>
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControl fullWidth>
              <TextField
                id="firstName"
                label={t('regFirstName')}
                error={!!errors.firstName}
                helperText={t(`onbStHlNameMessage`) as string}
                {...register('firstName', { required: true })}
                onChange={capitalizeName}
              />
              {errors.firstName && (
                <p className="text-orange-600 text-xs">
                  {t('regFirstNameValidation')}
                </p>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                id="lastName"
                label={t('regLastName')}
                error={!!errors.lastName}
                helperText={t(`onbStHlNameMessage`) as string}
                {...register('lastName', { required: true })}
              />
              {errors.lastName && (
                <p className="text-orange-600 text-xs">
                  {t('regLastNameValidation')}
                </p>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                id="secondLastName"
                label={t('regSecondLastName')}
                error={!!errors.secondLastName}
                helperText={t(`onbStHlNameMessage`) as string}
                disabled={!enableSecondLastName}
                {...register('secondLastName', {
                  required: enableSecondLastName,
                })}
              />
              {errors.secondLastName && (
                <p className="text-orange-600 text-xs">
                  {t('regSecondLastNameValidation')}
                </p>
              )}

              {data.country === Countries.USA && (
                <FormControlLabel
                  className="self-end"
                  control={
                    <Checkbox
                      onChange={() =>
                        setEnableSecondLastName(!enableSecondLastName)
                      }
                    />
                  }
                  label={t('regSecondLastNameCheckbox')}
                />
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="gender">{t('regGender')}</InputLabel>
              <Controller
                name="gender"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="gender"
                    id="gender"
                    label={t('regGender')}
                    error={!!errors.gender}
                    value={field.value || ''}
                  >
                    {[
                      { value: 'regGenderMale', key: 'Male' },
                      { value: 'regGenderFemale', key: 'Female' },
                    ].map((genderOption) => (
                      <MenuItem key={genderOption.key} value={genderOption.key}>
                        {t(genderOption.value)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.gender && (
                <p className="text-orange-600 text-xs">
                  {t('regGenderValidation')}
                </p>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Grid
          container
          className="mt-3"
          rowSpacing={2}
          columnSpacing={{ xs: 2, sm: 2, md: 3 }}
        >
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    className="w-full"
                    label={`${t('regWAPhone')}`}
                    value={value}
                    onChange={onChange}
                    error={!!errors.phone}
                    defaultCountry={(data.country as string)
                      .slice(0, 2)
                      .toLowerCase()}
                  />
                )}
              />
              {errors.phone && errors.phone.type === 'required' && (
                <p className="text-orange-600 text-xs">
                  {t('regPhoneValidation')}
                </p>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                id="email"
                label={t('regEmail')}
                error={!!errors.email || !!emailExist}
                {...register('email', {
                  onBlur: (e) => {
                    e.preventDefault();
                    validateRegisterEmail(e);
                  },
                  required: true,
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: t('validateEmail'),
                  },
                })}
              />
              {errors.email && (
                <p className="text-orange-600 text-xs">
                  {t('regEmailValidation')}
                </p>
              )}
              {emailExist && (
                <p className="text-orange-600 text-xs">{t(emailExist)}</p>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Controller
                name="countryBirthId"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, ...props } }) => {
                  const getSelectedCountry = () => {
                    if (defaultValues?.countryBirthId && birthcountries) {
                      return (
                        (birthcountries as CountriesList[])?.find(
                          (country) =>
                            country.id === Number(defaultValues?.countryBirthId)
                        ) || null
                      );
                    }
                    if (props.value && birthcountries) {
                      return (
                        (birthcountries as CountriesList[])?.find(
                          (country) => country.id === Number(props.value)
                        ) || null
                      );
                    }
                    return null;
                  };

                  return (
                    <Autocomplete
                      {...props}
                      disablePortal
                      getOptionLabel={(contact) => contact.name}
                      options={(birthcountries as CountriesList[]) ?? []}
                      value={getSelectedCountry()}
                      onChangeCapture={(e) =>
                        getBirthCountries(
                          (e as React.ChangeEvent<HTMLInputElement>).target
                            .value
                        )
                      }
                      onChange={(_, selectedOption) => {
                        onChange(selectedOption?.id);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={t('regBirthCountry')}
                          error={!!errors.countryBirthId}
                          autoComplete="off"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'off',
                          }}
                        />
                      )}
                      renderOption={(innerProps, option) => (
                        <Box component="li" className="px-3" {...innerProps}>
                          <div>{option.name}</div>
                        </Box>
                      )}
                    />
                  );
                }}
              />
              {errors.countryBirthId && (
                <p className="text-orange-600 text-xs">{t('defaultError')}</p>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Controller
                name="nationalityPersonId"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, ...props } }) => {
                  const getSelectedNationalityCountry = () => {
                    if (defaultValues?.nationalityPersonId && countries) {
                      setControlNationalityCountry(
                        (countries as CountriesList[])?.find(
                          (country) =>
                            country.id ===
                            Number(defaultValues?.nationalityPersonId)
                        ) || null
                      );
                      return (
                        (countries as CountriesList[])?.find(
                          (country) =>
                            country.id ===
                            Number(defaultValues?.nationalityPersonId)
                        ) || null
                      );
                    }
                    if (props.value && countries) {
                      return (
                        (countries as CountriesList[])?.find(
                          (country) => country.id === Number(props.value)
                        ) || null
                      );
                    }
                    return null;
                  };

                  return (
                    <Autocomplete
                      {...props}
                      disablePortal
                      getOptionLabel={(contact) => contact.name}
                      options={(countries as CountriesList[]) ?? []}
                      value={getSelectedNationalityCountry()}
                      onChangeCapture={(e) =>
                        getCountries(
                          (e as React.ChangeEvent<HTMLInputElement>).target
                            .value
                        )
                      }
                      onChange={(_, selectedOption) => {
                        setControlNationalityCountry(selectedOption);

                        onChange(selectedOption?.id);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={t('regNationality')}
                          error={!!errors.nationalityPersonId}
                          autoComplete="off"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'off',
                          }}
                        />
                      )}
                      renderOption={(innerProps, option) => (
                        <Box component="li" className="px-3" {...innerProps}>
                          <div>{option.name}</div>
                        </Box>
                      )}
                    />
                  );
                }}
              />
              {errors.nationalityPersonId && (
                <p className="text-orange-600 text-xs">{t('defaultError')}</p>
              )}
            </FormControl>
          </Grid>
          {controlNationalityCountry?.countryKey === CurrencyCountry.MXN ? (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Controller
                  name="federalEntityId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, ...props } }) => (
                    <Autocomplete
                      {...props}
                      disablePortal
                      getOptionLabel={(contact) => contact.description}
                      options={(federalEntities as FederalEntitiesList[]) ?? []}
                      value={
                        props.value
                          ? (federalEntities as FederalEntitiesList[])?.find(
                              (country) => country.id === Number(props.value)
                            )
                          : null
                      }
                      onChangeCapture={(e) =>
                        getFederalEntities(
                          (e as React.ChangeEvent<HTMLInputElement>).target
                            .value
                        )
                      }
                      onChange={(_, selectedOption) =>
                        onChange(selectedOption?.id)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={t('regFederalEntity')}
                          error={!!errors.federalEntityId}
                          autoComplete="off"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'off',
                          }}
                        />
                      )}
                      renderOption={(innerProps, option) => (
                        <Box component="li" className="px-3" {...innerProps}>
                          <div>{option.description}</div>
                        </Box>
                      )}
                    />
                  )}
                />
                {errors.federalEntityId && (
                  <p className="text-orange-600 text-xs">{t('defaultError')}</p>
                )}
              </FormControl>
            </Grid>
          ) : null}

          {data.country === Countries.MX && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="curp"
                  label={t('regCurp')}
                  helperText={
                    <>
                      {t(`regCurpHelper`)}{' '}
                      <Link href={PUBLIC_URLS.CURP_HELPER_URL} target="_blank">
                        {t('regCurpText')}
                      </Link>
                    </>
                  }
                  error={!!errors.curp}
                  {...register('curp', {
                    required: true,
                    pattern: CURP_REGEX,
                  })}
                  onChange={(event) => {
                    event.target.value = event.target.value.toUpperCase();
                  }}
                />
                {errors.curp && errors.curp.type === 'required' && (
                  <p className="text-orange-600 text-xs">{t('defaultError')}</p>
                )}
                {errors.curp && errors.curp.type === 'pattern' && (
                  <p className="text-orange-600 text-xs">
                    {t('regCurpValidation')}
                  </p>
                )}
              </FormControl>
            </Grid>
          )}

          {data.country === Countries.USA && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TextField
                  id="representativePosition"
                  label={t('regRepresentative')}
                  error={!!errors.representativePosition}
                  {...register('representativePosition', { required: true })}
                  onChange={capitalizeName}
                />
                {errors.lastName && (
                  <p className="text-orange-600 text-xs">
                    {t('requiredField')}
                  </p>
                )}
              </FormControl>
            </Grid>
          )}

          {isSubAccountEnabled && (
            <MasterAccountField
              organizationId={organizationId}
              errors={errors}
              register={register}
              t={t}
              onValidate={validateOrganization}
              isValidating={isValidatingMasterAccount}
              validationError={masterAccountValidationError}
              isValid={isMasterAccountValid}
            />
          )}
        </Grid>

        <Grid
          container
          flexDirection={{ xs: 'column', md: 'column' }}
          rowSpacing={2}
          columnSpacing={{ xs: 2, sm: 2, md: 3 }}
          className="mt-3"
        >
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <TextField
                id="companyName"
                label={t('regCompanyName')}
                error={!!errors.companyName}
                helperText={t('regCompanyNameHelper')}
                {...register('companyName', {
                  required: true,
                })}
              />
              {errors.companyName && (
                <p className="text-orange-600 text-xs">
                  {t('regCompanyNameValidation')}
                </p>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={12}>
            <Typography variant="h6">{t('referrerTitle')}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <input
                type="text"
                className="hidden"
                {...register('referralId')}
              />
              <TextField
                id="referral"
                label={t('referrerInputLabel')}
                {...register('referral')}
                inputRef={inputCode}
                onChange={validateReferralCode}
              />
            </FormControl>
            {validationError && (
              <span className="pt-1 block text-sm text-red-600">
                {t('referrerError')}
              </span>
            )}
            {validationSuccess && (
              <span className="pt-1 block text-sm text-green-600">
                {t('referrerSuccess')}
              </span>
            )}
          </Grid>

          <Grid item xs={12} md={12}>
            <Typography variant="h6">{t('regPasswordTitle')}</Typography>
          </Grid>

          <Grid item xs={12}>
            <PasswordInput
              control={control}
              setValue={setValue}
              name="password"
              confirmFieldName="confirmPassword"
              error={!!errors.password}
              errorMessage={t('regPasswordValidation') || undefined}
              confirmError={!!errors.confirmPassword}
              confirmErrorMessage={
                t('regConfirmPasswordValidation') || undefined
              }
              validationSchema={PASSWORD_SCHEMA}
              enforceValidation
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {error && (
            <Typography variant="body2" className="text-orange-600 text-xs">
              {t(error)}
            </Typography>
          )}
          {errorCode && (
            <Typography variant="body2" className="text-orange-600 text-xs">
              Code: {errorCode}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <Controller
              name="dataConfirm"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, ...field } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  }
                  label={t('regConfirmCheckBox')}
                />
              )}
            />
            {errors.dataConfirm && (
              <p className="text-orange-600 text-xs">{t('requiredField')}</p>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} className="flex justify-between">
          {formStep > 0 && (
            <Button
              appearance="secondary"
              className="mt-4"
              size="large"
              onClick={handleBack}
              type="button"
            >
              {t('regBackBtn')}
            </Button>
          )}
          <Button
            appearance="primary"
            size="large"
            className="mt-4"
            type="submit"
            disabled={isFormSubmissionDisabled}
          >
            {t('regContinue')}
          </Button>
        </Grid>
      </form>
    </>
  );
};

export default CompanyInformation;
